import type { HTMLAttributes } from 'react';
import React from 'react';
import type { ClassNameProp, EHealthIcon, MaterialIcon } from '..';
import { isEmptyString } from '../../util';

/**
 * Type for a valid name of an Icon.
 * This includes EHealthIcons and MaterialIcons.
 * @author agd
 */
export type ValidIconName = EHealthIcon | MaterialIcon;

/**
 * Props for an {@link Icon} component.
 * @author agd
 */
export interface IconProps
  extends ClassNameProp,
    Pick<HTMLAttributes<HTMLElement>, 'aria-hidden' | 'role' | 'style'> {
  /**
   * {@link HTMLAttributes#aria-label}, mandatory attribute for {@link Icon}.
   */
  'aria-label': HTMLAttributes<HTMLElement>['aria-label'];
  /**
   * Name of the icon.
   */
  iconName: ValidIconName;
}

const EHEALTH_ICON_PREFIX = 'ehealthicon-';
const MATERIAL_ICON_PREFIX = 'materialicon-';

/**
 * A simple Icon element.
 *
 * @param props icon properties the <code>'ehealthicon-'</code> prefix should be omitted from the iconName if the icon is hard-coded
 * @constructor
 * @return {@link JSX.Element} created for the icon.
 */
export function Icon({
  className = '',
  iconName,
  style,
  'aria-label': ariaLabel,
  role = isEmptyString(ariaLabel) ? undefined : 'img',
  'aria-hidden': ariaHidden = isEmptyString(ariaLabel)
}: IconProps): JSX.Element {
  // this is needed for icons returned at runtime from the backend
  if (!iconName.startsWith(MATERIAL_ICON_PREFIX)) {
    // ensures that ehealthicon- prefix is assigned for iconName without materialicon- prefix
    iconName = iconName.replace(
      RegExp('(' + EHEALTH_ICON_PREFIX + ')?'),
      EHEALTH_ICON_PREFIX
    ) as EHealthIcon;
  }
  const iconClassName = ['ehs-icon', className, iconName]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      style={style}
      className={iconClassName}
      role={role}
      aria-label={isEmptyString(ariaLabel) ? undefined : ariaLabel}
      aria-hidden={ariaHidden}
    />
  );
}
